import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../components/molecules/Modal.jsx';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  makeStyles,
  MenuItem,
  TextField
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { SelectField } from '../../components/molecules/SelectField.jsx';
import { TextFormField } from '../../components/atoms/form/TextFormField.jsx';
import { transformSnake2Camel } from '../../queries/utils.js';
import { inspectionTypes, orderPriorities } from './constants.js';
import { FormattedMessage, useIntl } from 'react-intl';
import { DatePicker } from '../../components/molecules/DatePicker.jsx';
import { INSPECTION_TYPES } from '../Inspections/Inspections.jsx';
import { lightThemeV2 } from '../../settings/themes/lightThemeV2.js';
import { useGetProjects } from '../../queries/queryProjects.js';
import { DEFAULT_PARAMS } from '../Programs/index.js';
import { useGetFormularies } from '../../queries/queryInspection.js';
import { useGetUsers } from '../../queries/queryUsers.js';

const useStyles = makeStyles({
  buttonControls: {
    display: 'flex',
    gap: lightThemeV2.spacing(2)
  },
  buttonContainer: {
    marginTop: lightThemeV2.spacing(2),
    alignItems: 'center'
  }
});

export const WorkOrderModal = ({ open, onClose, onSave, orderToEdit, clientId }) => {
  const classes = useStyles();
  const { messages: intlMessages } = useIntl();

  const { data: projectsResponse = {}, isLoading: isLoadingProjects } = useGetProjects(
    { clientId, params: DEFAULT_PARAMS },
    { enabled: open }
  );
  const { data: formularies = {} } = useGetFormularies({ clientId }, { enabled: open });
  const { data: users, isLoading: isLoadingWorkOrdersUsers } = useGetUsers({ clientId }, { enabled: open });

  const [details, setDetails] = React.useState({
    description: '',
    assigneeId: '',
    projectId: '',
    priority: 3,
    status: 'todo',
    formularyId: null,
    expectedCompletionDate: new Date()
  });
  const [inspectionTypeSelected, setInspectionTypeSelected] = React.useState('route');

  React.useEffect(() => {
    if (orderToEdit) {
      setDetails({
        description: orderToEdit.description || '',
        assigneeId: orderToEdit.assigneeId || '',
        projectId: orderToEdit.projectId || '',
        priority: orderToEdit.priority || 3,
        status: orderToEdit.status || 'todo',
        formularyId: orderToEdit.formularyId || null,
        expectedCompletionDate: orderToEdit.expectedCompletionDate ? new Date(orderToEdit.expectedCompletionDate) : new Date()
      });

      if (orderToEdit.formulary_id) {
        setInspectionTypeSelected(INSPECTION_TYPES.FORMULARY);
      }
    } else {
      setDetails({
        description: '',
        assigneeId: '',
        projectId: '',
        priority: 3,
        status: 'todo',
        formularyId: null,
        expectedCompletionDate: new Date()
      });
      setInspectionTypeSelected('route');
    }
  }, [orderToEdit]);

  const [createAnother, setCreateAnother] = React.useState(false);

  const projects = projectsResponse.projects || [];
  const normalizedUsers = transformSnake2Camel(users || []);
  const normalizedFormularies = transformSnake2Camel(formularies);
  const activeUsers = normalizedUsers.filter(user => user.active);
  const selectedUser = activeUsers.find(user => user.id === details.assigneeId) || null;

  const handleUserChange = (event, newValue) => {
    setDetails(prev => ({
      ...prev,
      assigneeId: newValue ? newValue.id : null
    }));
  };

  const handleProjectChange = (event, newValue) => {
    setDetails(prev => ({
      ...prev,
      projectId: newValue ? newValue.id : null,
      formularyId: null
    }));
    setInspectionTypeSelected('route');
  };

  const handleTypeSelection = e => {
    const inspectionTypeSelected = e.target.value;

    setDetails(prev => ({ ...prev, formularyId: null }));
    setInspectionTypeSelected(inspectionTypeSelected);
  };

  const handleFormularySelection = e => {
    const formularyId = e.target.value;
    const formulary = normalizedFormularies.find(f => f.id === formularyId);

    setDetails(prev => ({ ...prev, formulary, formularyId }));
  };

  const handleDateChange = date => {
    setDetails(prev => ({ ...prev, expectedCompletionDate: date && date.toString() }));
  };
  const isValid = () => {
    return details.description && details.assigneeId && details.projectId && details.expectedCompletionDate;
  };

  const handleSave = () => {
    if (isValid()) {
      const orderData = {
        description: details.description,
        assignee_id: details.assigneeId,
        project_id: details.projectId,
        priority: details.priority,
        status: details.status,
        formulary_id: details.formularyId,
        expected_completion_date: details.expectedCompletionDate
      };

      if (orderToEdit && orderToEdit.id) {
        orderData.id = orderToEdit.id;
      }

      onSave({
        workOrder: orderData,
        close: true
      });

      if (!createAnother) {
        onClose();
      }
    }
  };

  return (
    <Modal title={<FormattedMessage id={orderToEdit ? 'workOrders.editing' : 'workOrders.creating'} />} open={open} onClose={onClose}>
      <Grid container style={{ gap: '18px' }}>
        <Grid item xs={12}>
          <TextFormField
            label={intlMessages['common.description']}
            variant="outlined"
            value={details.description}
            onChange={value => setDetails(prev => ({ ...prev, description: value }))}
            multiline
          />
        </Grid>
        <Grid item xs={12}>
          {
            <Autocomplete
              options={activeUsers}
              getOptionLabel={user => user.fullName}
              value={selectedUser}
              onChange={handleUserChange}
              getOptionSelected={(option, value) => option.id === value.id}
              renderInput={params => {
                const props = {
                  ...params,
                  label: intlMessages['workOrders.attributes.assignee'],
                  variant: 'outlined',
                  InputLabelProps: { shrink: true }
                };

                if (isLoadingWorkOrdersUsers) {
                  props.InputProps = {
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress color="inherit" size={20} />
                      </InputAdornment>
                    )
                  };
                }
                return <TextField {...props} />;
              }}
            />
          }
        </Grid>
        <Grid item xs={12}>
          <SelectField
            label={<FormattedMessage id="workOrders.attributes.priority" />}
            value={details.priority}
            onChange={e => setDetails({ ...details, priority: Number(e.target.value) })}
          >
            {orderPriorities.map(option => (
              <MenuItem key={option.id} value={option.id}>
                <FormattedMessage id={option.i18nId} />
              </MenuItem>
            ))}
          </SelectField>
        </Grid>

        <Grid item xs={12}>
          {
            <Autocomplete
              options={projects}
              getOptionLabel={project => project.name || ''}
              getOptionSelected={(option, value) => option.id === value}
              value={projects.find(proj => proj.id === details.projectId) || null}
              onChange={handleProjectChange}
              renderInput={params => {
                const props = {
                  ...params,
                  label: intlMessages['beneficiaries.associateProject'],
                  variant: 'outlined',
                  InputLabelProps: { shrink: true }
                };

                if (isLoadingProjects) {
                  props.InputProps = {
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress color="inherit" size={20} />
                      </InputAdornment>
                    )
                  };
                }

                return <TextField {...props} />;
              }}
            />
          }
        </Grid>

        {!!details.projectId && !!normalizedFormularies.length && (
          <>
            <Grid item xs={12}>
              <SelectField
                value={inspectionTypeSelected}
                onChange={handleTypeSelection}
                fullWidth
                label={intlMessages['workOrders.attributes.inspectionType']}
              >
                {inspectionTypes.map(option => (
                  <MenuItem key={option.type} value={option.type}>
                    <FormattedMessage id={option.i18nId} />
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>

            {inspectionTypeSelected === INSPECTION_TYPES.FORMULARY && (
              <Grid item xs={12}>
                <SelectField
                  value={details.formularyId || ''}
                  onChange={handleFormularySelection}
                  fullWidth
                  label={intlMessages['workOrders.attributes.formulary']}
                >
                  {normalizedFormularies.map(option => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
            )}
          </>
        )}
        <Grid item xs={12}>
          <DatePicker
            label={<FormattedMessage id="workOrders.attributes.expected_completion_date" />}
            value={details.expectedCompletionDate}
            onChange={handleDateChange}
            minDate={new Date()}
          />
        </Grid>
      </Grid>
      <Grid container justifyContent="space-between" className={classes.buttonContainer}>
        <Grid item className={classes.buttonControls}>
          <Grid item>
            <Button variant="contained" onClick={handleSave} disabled={!isValid()}>
              <FormattedMessage id={orderToEdit ? 'common.edit' : 'common.save'} />
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={() => onClose()}>
              <FormattedMessage id="common.cancel" />
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={<Checkbox checked={createAnother} onChange={e => setCreateAnother(e.target.checked)} color="primary" />}
            label={<FormattedMessage id="workOrders.createAnother" />}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

WorkOrderModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  orderToEdit: PropTypes.object,
  clientId: PropTypes.number
};
